import { Input, Select } from '@trackman/web-shared-components';
import Switch from 'components/Switch/Switch';
import { UmpireSearch } from 'components/UmpireSearch/UmpireSearch';
import { ReactComponent as LockIcon } from 'components/icons/lockIcon.svg';
import { ReactComponent as UnlockIcon } from 'components/icons/unlockIcon.svg';
import { SelectWrapper } from 'components/ui/SelectWrapper/SelectWrapper';
import { ServiceTypes } from 'constants/ServiceTypes';
import { IOwningOrganization } from 'interfaces/session/IOwningOrganization';
import { IField, ILeague, ILevel, ISession, IVenue } from 'interfaces/session/ISession';
import { ISearchTeam } from 'interfaces/team/ISearchTeam';
import { IUmpire } from 'interfaces/umpire/IUmpire';
import { TeamSelector } from 'pages/Sessions/Components/TeamSelector';
import React, { useEffect, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import { TeamStore } from 'stores/TeamStore';
import {
  EditInputsContainer,
  EditTeams,
  EditWrapper,
  SmallTitle,
  StyledClearIcon,
  StyledSwitchIcon,
  TeamsContainer,
  TeamsDivider,
  TeamSelectContainer,
  TeamSelectorWrapper,
  TeamTitleWrapper,
  TeamWrapper,
} from './TeamsAndSettingsStyles';
interface IProps {
  session?: ISession;
  visible: boolean;
  selectedLeague: ILeague;
  setSelectedLeague: React.Dispatch<React.SetStateAction<ILeague>>;
  selectedLevel: ILevel;
  setSelectedLevel: React.Dispatch<React.SetStateAction<ILevel>>;
  selectedField: IField;
  setSelectedField: React.Dispatch<React.SetStateAction<IField>>;
  selectedVenue: IVenue;
  setSelectedVenue: React.Dispatch<React.SetStateAction<IVenue>>;
  selectedHomeTeam?: ISearchTeam;
  setSelectedHomeTeam: React.Dispatch<React.SetStateAction<ISearchTeam | undefined>>;
  selectedAwayTeam?: ISearchTeam;
  setSelectedAwayTeam: React.Dispatch<React.SetStateAction<ISearchTeam | undefined>>;
  selectedOrg?: IOwningOrganization;
  setSelectedOrg: React.Dispatch<React.SetStateAction<IOwningOrganization | undefined>>;
  selectedUmpire?: IUmpire;
  setSelectedUmpire: (umpire?: IUmpire) => void;
  owningOrganizations: IOwningOrganization[];
  dailySequenceNumber?: number;
  setDailySequenceNumber: React.Dispatch<React.SetStateAction<number | undefined>>;
  externalSessionId?: string;
  setExternalSessionId: React.Dispatch<React.SetStateAction<string | undefined>>;
  invalidExternalId: boolean;
  setInvalidExternalId: React.Dispatch<React.SetStateAction<boolean>>;
  externalEventId?: string;
}

const TeamsAndSettings: React.FC<IProps> = (props) => {
  const {
    session,
    visible,
    selectedLeague,
    setSelectedLeague,
    selectedLevel,
    setSelectedLevel,
    selectedField,
    setSelectedField,
    selectedVenue,
    setSelectedVenue,
    selectedHomeTeam,
    setSelectedHomeTeam,
    selectedAwayTeam,
    setSelectedAwayTeam,
    selectedOrg,
    setSelectedOrg,
    selectedUmpire,
    setSelectedUmpire,
    owningOrganizations,
    dailySequenceNumber,
    setDailySequenceNumber,
    externalSessionId,
    setExternalSessionId,
    invalidExternalId,
    setInvalidExternalId,
    externalEventId,
  } = props;
  const teamStore = useInjection<TeamStore>(ServiceTypes.TeamStore);
  const sessionStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
  const [isEditTeamsLocked, setIsEditTeamsLocked] = useState<boolean>(true);

  const contextualDataToDropdownMap = (obj: ILeague | ILevel | IOwningOrganization | IField | IVenue) => ({
    label: obj.name,
    value: obj.id,
  });

  const unlockTeamEdit = async () => {
    if (teamStore.teams.length === 0) {
      await teamStore.fetchTeams();
    }
    setIsEditTeamsLocked((prevState) => !prevState);
  };

  const switchTeams = () => {
    const awayTeamCpy = selectedAwayTeam ? { ...selectedAwayTeam } : undefined;
    setSelectedAwayTeam(selectedHomeTeam);
    setSelectedHomeTeam(awayTeamCpy);
  };

  useEffect(() => {
    if (!isEditTeamsLocked && !teamStore.isLoading && teamStore.teams.length === 0) {
      teamStore.fetchTeams();
    }
  }, [isEditTeamsLocked]);

  useEffect(() => {
    if (
      session &&
      owningOrganizations.length > 0 &&
      session.owningOrganizationId &&
      owningOrganizations.some((org) => org.id === session.owningOrganizationId)
    ) {
      setSelectedOrg(owningOrganizations.find((x) => x.id === session.owningOrganizationId));
    }
  }, [session, owningOrganizations]);

  const [homeTeamInputValue, setHomeTeamInputValue] = useState<string>('');
  const [awayTeamInputValue, setAwayTeamInputValue] = useState<string>('');

  return (
    <>
      {!session || !visible ? null : (
        <TeamsContainer>
          <SmallTitle margin='26px 0 7px 0'>
            <EditWrapper>
              <TeamTitleWrapper>
                <div>Teams</div>
                <TeamsDivider />
                <EditTeams>
                  <Switch
                    checked={!isEditTeamsLocked}
                    reversed
                    onClick={unlockTeamEdit}
                    leftContent={<LockIcon />}
                    rightContent={<UnlockIcon />}
                  />
                </EditTeams>
              </TeamTitleWrapper>
            </EditWrapper>
          </SmallTitle>

          <TeamSelectContainer>
            <TeamWrapper>
              <TeamSelectorWrapper disabled={isEditTeamsLocked}>
                <StyledClearIcon
                  onClick={() => {
                    if (!isEditTeamsLocked) {
                      setSelectedAwayTeam(undefined);
                      setAwayTeamInputValue('');
                    }
                  }}
                />
                <TeamSelector
                  label='Away Team'
                  onSelectionChange={(selectedTeam) => setSelectedAwayTeam(selectedTeam)}
                  isDisabled={teamStore.isLoading || isEditTeamsLocked}
                  selectedTeam={selectedAwayTeam}
                  inputValue={awayTeamInputValue}
                  setInputValue={setAwayTeamInputValue}
                  isError={!selectedAwayTeam}
                />
              </TeamSelectorWrapper>
              <Input
                label='External event team ID'
                type='text'
                value={session.awayTeam.externalId}
                onChange={() => {}}
                disabled
                width={'416px'}
                margin={'5px 0px'}
              />
            </TeamWrapper>
            <StyledSwitchIcon onClick={() => !isEditTeamsLocked && switchTeams()} disabled={teamStore.isLoading || isEditTeamsLocked} />
            <TeamWrapper>
              <TeamSelectorWrapper disabled={isEditTeamsLocked}>
                <StyledClearIcon
                  onClick={() => {
                    if (!isEditTeamsLocked) {
                      setSelectedHomeTeam(undefined);
                      setHomeTeamInputValue('');
                    }
                  }}
                />{' '}
                <TeamSelector
                  label='Home Team'
                  onSelectionChange={(selectedTeam) => {
                    setSelectedHomeTeam(selectedTeam);
                  }}
                  isDisabled={teamStore.isLoading || isEditTeamsLocked}
                  selectedTeam={selectedHomeTeam}
                  inputValue={homeTeamInputValue}
                  setInputValue={setHomeTeamInputValue}
                  isError={!selectedHomeTeam}
                />
              </TeamSelectorWrapper>
              <Input
                label='External event team ID'
                type='text'
                value={session.homeTeam.externalId}
                onChange={() => {}}
                disabled
                width={'416px'}
                margin={'5px 0px'}
              />
            </TeamWrapper>
          </TeamSelectContainer>
          <SmallTitle margin='30px 0 16px 0'>Settings</SmallTitle>
          <EditInputsContainer>
            <SelectWrapper>
              <Select
                label='Level'
                selectOptions={sessionStore.levels.map(contextualDataToDropdownMap)}
                value={(selectedLevel && contextualDataToDropdownMap(selectedLevel).value) || ''}
                onChange={(event: any) => {
                  setSelectedLevel(sessionStore.levels.find((x) => x.id === event.target.value)!);
                }}
                width='288px'
                required
                error={!selectedLevel}
                margin={'0 0 16px 0'}
              />
            </SelectWrapper>
            <SelectWrapper>
              <Select
                label='League'
                selectOptions={sessionStore.leagues.map(contextualDataToDropdownMap)}
                value={(selectedLeague && contextualDataToDropdownMap(selectedLeague).value) || ''}
                onChange={(event: any) => {
                  setSelectedLeague(sessionStore.leagues.find((x) => x.id === event.target.value)!);
                }}
                width='288px'
                required
                error={!selectedLeague}
                margin={'0 0 16px 0'}
              />
            </SelectWrapper>
            <SelectWrapper>
              <Select
                label='Venue'
                selectOptions={sessionStore.venues.map(contextualDataToDropdownMap)}
                value={(selectedVenue && contextualDataToDropdownMap(selectedVenue).value) || ''}
                onChange={(event: any) => {
                  setSelectedVenue(sessionStore.venues.find((x) => x.id === event.target.value)!);
                  setSelectedField({} as IField);
                }}
                width='288px'
                margin={'0 0 16px 0'}
              />
            </SelectWrapper>
          </EditInputsContainer>
          <EditInputsContainer>
            <SelectWrapper>
              <Select
                label='Field'
                selectOptions={sessionStore.fields
                  .filter((f) => selectedVenue.fields && selectedVenue.fields.some((id) => f.id === id))
                  .map(contextualDataToDropdownMap)}
                value={(selectedField && contextualDataToDropdownMap(selectedField).value) || ''}
                onChange={(event: any) => {
                  setSelectedField(sessionStore.fields.find((x) => x.id === event.target.value)!);
                }}
                width='288px'
                margin={'5px 0px'}
              />
            </SelectWrapper>
            <SelectWrapper>
              <Select
                label='Owning organization'
                selectOptions={owningOrganizations.map(contextualDataToDropdownMap)}
                value={(selectedOrg && contextualDataToDropdownMap(selectedOrg).value) || ''}
                onChange={(event: any) => {
                  setSelectedOrg(owningOrganizations.find((x) => x.id === event.target.value)!);
                }}
                width='288px'
                margin={'5px 0px'}
              />
            </SelectWrapper>
            <UmpireSearch selectedUmpire={selectedUmpire} setSelectedUmpire={setSelectedUmpire} width={'288px'} margin={'5px 0px'} />
          </EditInputsContainer>
          <SmallTitle margin='30px 0 16px 0'>Game reference settings</SmallTitle>
          <EditInputsContainer>
            <Input
              label='Game reference'
              type='text'
              value={session.gameReference || ''}
              onChange={() => {}}
              disabled
              width={'288px'}
              margin={'5px 0px'}
            />
            <Input
              label='Sequence number'
              type='number'
              value={dailySequenceNumber || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (parseInt(event.target.value) > 0) {
                  setDailySequenceNumber(parseInt(event.target.value));
                }
              }}
              width={'185px'}
              margin={'5px 0px'}
            />
            <Input
              label='External ID'
              type='text'
              value={externalSessionId || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setExternalSessionId(e.target.value);
                setInvalidExternalId(false);
              }}
              width={'185px'}
              margin={'5px 0px'}
              required
              error={invalidExternalId}
            />
            <Input
              label='Event ID'
              type='text'
              value={externalEventId || ''}
              onChange={() => {}}
              width={'185px'}
              margin={'5px 0px'}
              disabled
            />
          </EditInputsContainer>
        </TeamsContainer>
      )}
    </>
  );
};

export default TeamsAndSettings;
